import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { BASE_URL } from '../services/api';

/**
 * PhotoLightbox - Full-screen photo viewer with navigation
 * Props:
 *   photos: array of { id, filename, file_path, original_name }
 *   initialIndex: number (index to open at)
 *   onClose: fn
 */
const getPhotoUrl = (photo) => {
    if (!photo) return '';
    // If it's a full URL (legacy Cloudinary), return as is
    if (photo.file_path && photo.file_path.startsWith('http')) {
        return photo.file_path;
    }
    // If it's a relative path (new local storage), prepend BASE_URL
    const path = photo.file_path || `/uploads/photos/${photo.filename}`;
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${BASE_URL}${cleanPath}`;
};

const PhotoLightbox = ({ photos = [], initialIndex = 0, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoomed, setZoomed] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const total = photos.length;
    const current = photos[currentIndex];

    const goTo = useCallback((index) => {
        if (index < 0 || index >= total) return;
        setCurrentIndex(index);
        setZoomed(false);
        setImageLoaded(false);
    }, [total]);

    const goNext = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);
    const goPrev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrev();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [goNext, goPrev, onClose]);

    // Lock body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleDownload = () => {
        const url = getPhotoUrl(current);
        const a = document.createElement('a');
        a.href = url;
        a.download = current.original_name || `photo_${currentIndex + 1}.jpg`;
        a.target = '_blank';
        a.click();
    };

    if (!photos.length) return null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 99999,
                background: 'rgba(0,0,0,0.93)',
                display: 'flex', flexDirection: 'column',
                animation: 'fadeIn 0.2s ease',
                backdropFilter: 'blur(6px)',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            {/* ── Header ── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px',
                background: 'rgba(0,0,0,0.6)',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                flexShrink: 0,
            }}>
                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontWeight: '600' }}>
                    {currentIndex + 1} / {total} photo{total > 1 ? 's' : ''}
                </span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: 'white', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {current?.original_name || `Constatation #${currentIndex + 1}`}
                </span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {/* Zoom toggle */}
                    <button
                        onClick={() => setZoomed(z => !z)}
                        title={zoomed ? 'Réduire' : 'Agrandir'}
                        style={{
                            background: zoomed ? 'rgba(200,164,55,0.2)' : 'rgba(255,255,255,0.08)',
                            border: zoomed ? '1px solid rgba(200,164,55,0.5)' : '1px solid rgba(255,255,255,0.15)',
                            color: zoomed ? '#c8a437' : 'white',
                            width: '36px', height: '36px', borderRadius: '8px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s',
                        }}
                    >
                        {zoomed ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
                    </button>
                    {/* Download */}
                    <button
                        onClick={handleDownload}
                        title="Télécharger"
                        style={{
                            background: 'rgba(200,164,55,0.15)',
                            border: '1px solid rgba(200,164,55,0.3)',
                            color: '#c8a437',
                            width: '36px', height: '36px', borderRadius: '8px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s',
                        }}
                    >
                        <Download size={16} />
                    </button>
                    {/* Close */}
                    <button
                        onClick={onClose}
                        title="Fermer (Échap)"
                        style={{
                            background: 'rgba(239,68,68,0.15)',
                            border: '1px solid rgba(239,68,68,0.3)',
                            color: '#ef4444',
                            width: '36px', height: '36px', borderRadius: '8px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s',
                        }}
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* ── Main image area ── */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', padding: '16px 80px' }}>
                {/* Prev */}
                <button
                    onClick={goPrev}
                    disabled={currentIndex === 0}
                    style={{
                        position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
                        width: '52px', height: '52px', borderRadius: '50%',
                        background: currentIndex === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: 'white', cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: currentIndex === 0 ? 0.3 : 1,
                        transition: 'all 0.2s',
                        zIndex: 10,
                    }}
                    onMouseEnter={e => { if (currentIndex > 0) e.currentTarget.style.background = 'rgba(200,164,55,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = currentIndex === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.12)'; }}
                >
                    <ChevronLeft size={26} />
                </button>

                {/* Image */}
                <div style={{
                    maxWidth: '100%', maxHeight: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: zoomed ? 'auto' : 'hidden',
                }}>
                    {!imageLoaded && (
                        <div style={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div className="spinner" style={{ borderColor: 'rgba(255,255,255,0.2)', borderTopColor: '#c8a437', width: '40px', height: '40px' }} />
                        </div>
                    )}
                    <img
                        key={currentIndex}
                        src={getPhotoUrl(current)}
                        alt={current?.original_name || `Photo ${currentIndex + 1}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={(e) => { e.target.style.opacity = '0.3'; setImageLoaded(true); }}
                        style={{
                            maxWidth: zoomed ? 'none' : '100%',
                            maxHeight: zoomed ? 'none' : 'calc(100vh - 220px)',
                            width: zoomed ? 'auto' : 'auto',
                            height: zoomed ? 'auto' : 'auto',
                            objectFit: 'contain',
                            borderRadius: '10px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                            opacity: imageLoaded ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                            cursor: zoomed ? 'zoom-out' : 'zoom-in',
                            animation: 'imgFadeIn 0.3s ease',
                        }}
                        onClick={() => setZoomed(z => !z)}
                    />
                </div>

                {/* Next */}
                <button
                    onClick={goNext}
                    disabled={currentIndex === total - 1}
                    style={{
                        position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)',
                        width: '52px', height: '52px', borderRadius: '50%',
                        background: currentIndex === total - 1 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: 'white', cursor: currentIndex === total - 1 ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: currentIndex === total - 1 ? 0.3 : 1,
                        transition: 'all 0.2s',
                        zIndex: 10,
                    }}
                    onMouseEnter={e => { if (currentIndex < total - 1) e.currentTarget.style.background = 'rgba(200,164,55,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = currentIndex === total - 1 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.12)'; }}
                >
                    <ChevronRight size={26} />
                </button>

                {/* Keyboard hint */}
                <div style={{
                    position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)',
                    fontSize: '10px', color: 'rgba(255,255,255,0.25)',
                    background: 'rgba(0,0,0,0.4)', padding: '3px 10px', borderRadius: '20px',
                    pointerEvents: 'none', fontWeight: '500'
                }}>
                    ← → pour naviguer &nbsp;•&nbsp; Clic pour zoomer &nbsp;•&nbsp; Échap pour fermer
                </div>
            </div>

            {/* ── Thumbnail strip ── */}
            {total > 1 && (
                <div style={{
                    padding: '10px 20px 16px',
                    background: 'rgba(0,0,0,0.5)',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    flexShrink: 0,
                }}>
                    <div style={{
                        display: 'flex', gap: '6px', justifyContent: 'center',
                        overflowX: 'auto', paddingBottom: '2px',
                    }}>
                        {photos.map((p, i) => (
                            <div
                                key={p.id || i}
                                onClick={() => goTo(i)}
                                style={{
                                    width: '56px', height: '42px', flexShrink: 0,
                                    borderRadius: '6px', overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: i === currentIndex ? '2px solid #c8a437' : '2px solid transparent',
                                    opacity: i === currentIndex ? 1 : 0.45,
                                    transition: 'all 0.2s',
                                    boxShadow: i === currentIndex ? '0 0 10px rgba(200,164,55,0.5)' : 'none',
                                }}
                                onMouseEnter={e => { if (i !== currentIndex) e.currentTarget.style.opacity = '0.75'; }}
                                onMouseLeave={e => { if (i !== currentIndex) e.currentTarget.style.opacity = '0.45'; }}
                            >
                                <img
                                    src={getPhotoUrl(p)}
                                    alt={`Miniature ${i + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhotoLightbox;
